import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { usePermissions, type AppRole } from '../auth/usePermissions'
import { useAuth } from '../auth/AuthProvider'
import { useEffectivePermissions } from '../auth/useEffectivePermissions'

interface RoleGuardProps {
  /**
   * Module id (matches registry.ts entries + ops.RoleModuleAccess.module_id).
   * When provided, the guard resolves access via ops.RoleModuleAccess (DB)
   * rather than registry's hardcoded role list. This is the new path —
   * preferred over `roles`.
   */
  module?: string
  /**
   * Operation gated by `module`. Defaults to 'read' (the typical sidebar
   * + route nav case).
   */
  op?: 'read' | 'write'
  /**
   * @deprecated Static role list. Kept for backward compat during the
   * migration to DB-driven module access; will be removed once every call
   * site uses `module` instead. New code MUST use `module`.
   */
  roles?: AppRole[]
  children: ReactNode
}

function LoadingSpinner() {
  const { t } = useTranslation('shell')
  return (
    <div className="flex items-center justify-center h-64 gap-3 text-on-surface-variant">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      <span className="text-sm">{t('auth.loading')}</span>
    </div>
  )
}

function AccessDenied() {
  const { t } = useTranslation('shell')
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-on-surface-variant">
      <span className="material-symbols-outlined text-4xl text-outline-variant">lock</span>
      <p className="font-headline font-bold text-on-surface">{t('auth.accessDenied')}</p>
      <p className="text-sm">{t('auth.accessDeniedDetail')}</p>
    </div>
  )
}

function LoginPrompt() {
  const { login } = useAuth()
  const { t } = useTranslation('shell')
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4 text-on-surface-variant">
      <span className="material-symbols-outlined text-4xl text-outline-variant">account_circle</span>
      <p className="font-headline font-bold text-on-surface">{t('auth.loginRequired')}</p>
      <button
        onClick={() => login()}
        className="px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
      >
        {t('auth.loginWith')}
      </button>
    </div>
  )
}

export default function RoleGuard({ module, op = 'read', roles, children }: RoleGuardProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const { hasAnyRole } = usePermissions()
  const perms = useEffectivePermissions()

  if (isLoading || perms.isLoading) return <LoadingSpinner />
  if (!isAuthenticated) return <LoginPrompt />

  // Preferred path: DB-driven module access. IT.Admin sentinel bypasses.
  if (module) {
    const granted = op === 'write' ? perms.canWrite(module) : perms.canRead(module)
    if (!granted) return <AccessDenied />
    return <>{children}</>
  }

  // Legacy path: hardcoded roles array. Kept until every caller migrates to
  // `module`. New code MUST NOT use this branch.
  if (roles && roles.length > 0 && !hasAnyRole(...roles)) return <AccessDenied />

  return <>{children}</>
}
